import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Grid,
} from '@mui/material';
import {
    AddCircle as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Update as UpdateIcon,
    FilterList as FilterIcon,
    KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import TopNavBar from "../../components/TopNavBar/DefaultNavBar";
import { useCimbeo } from "../../context";

function RunHistory() {
    const { projectName } = useCimbeo();
    
    // Mock data - replace with actual API calls
    const [history, setHistory] = useState([]);
    const [filters, setFilters] = useState({
        type: 'all'
    });

    useEffect(() => {
        // Mock data - replace with actual API call
        const mockHistoryData = [
            {
                id: 1,
                timestamp: new Date(Date.now() - 1000000),
                type: 'artifact_added',
                project: 'Project 1',
                details: 'Added new artifact: Requirements Document v1.0',
                user: 'John Doe'
            },
            {
                id: 2,
                timestamp: new Date(Date.now() - 2000000),
                type: 'dataset_rerun',
                project: 'Project 2',
                details: 'Reprocessed dataset: Training Data 2023',
                user: 'Jane Smith'
            },
            {
                id: 3,
                timestamp: new Date(Date.now() - 3000000),
                type: 'artifact_removed',
                project: 'Project 1',
                details: 'Removed artifact: Outdated Specifications',
                user: 'John Doe'
            },
            {
                id: 4,
                timestamp: new Date(Date.now() - 4000000),
                type: 'project_updated',
                project: 'Project 3',
                details: 'Updated project settings and parameters',
                user: 'Mike Johnson'
            }
        ];

        // Filter history for the selected project
        const projectHistory = projectName 
            ? mockHistoryData.filter(item => item.project === projectName)
            : mockHistoryData;
        
        setHistory(projectHistory);
    }, [projectName]);

    const getActionIcon = (type) => {
        switch (type) {
            case 'artifact_added':
                return <AddIcon sx={{ color: '#4CAF50' }} />;
            case 'artifact_removed':
                return <DeleteIcon sx={{ color: '#F44336' }} />;
            case 'dataset_rerun':
                return <RefreshIcon sx={{ color: '#2196F3' }} />;
            case 'project_updated':
                return <UpdateIcon sx={{ color: '#FF9800' }} />;
            default:
                return <UpdateIcon />;
        }
    };

    const getActionColor = (type) => {
        switch (type) {
            case 'artifact_added':
                return '#4CAF50';
            case 'artifact_removed':
                return '#F44336';
            case 'dataset_rerun':
                return '#2196F3';
            case 'project_updated':
                return '#FF9800';
            default:
                return '#757575';
        }
    };

    const formatTimestamp = (date) => {
        return new Date(date).toLocaleString();
    };

    const formatActionType = (type) => {
        return type.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const filteredHistory = history.filter(item => {
        if (filters.type !== 'all' && item.type !== filters.type) return false;
        return true;
    });

    const actionTypes = [...new Set(history.map(item => item.type))];

    return (
        <DashboardLayout>
            <TopNavBar />
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom component="h1">
                    {projectName ? `${projectName} - Run History` : 'All Projects - Run History'}
                </Typography>
                
                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 3,
                            pl: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterIcon sx={{ color: '#2B5587', mr: 1 }} />
                            <Typography variant="h6" component="span">
                                Filters
                            </Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Action Type</InputLabel>
                            <Select
                                value={filters.type}
                                label="Action Type"
                                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                                IconComponent={() => (
                                    <ArrowDownIcon 
                                        sx={{ 
                                            color: '#2B5587',
                                            right: 8,
                                            position: 'absolute',
                                            pointerEvents: 'none',
                                            fontSize: 24
                                        }} 
                                    />
                                )}
                                sx={{
                                    height: 45,
                                    '& .MuiSelect-icon': {
                                        display: 'none' // Hide the default icon
                                    },
                                    pr: 4 // Add padding for the custom icon
                                }}
                            >
                                <MenuItem value="all">All Actions</MenuItem>
                                {actionTypes.map(type => (
                                    <MenuItem key={type} value={type}>{formatActionType(type)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>

                {/* History Timeline */}
                <Paper sx={{ p: 2 }}>
                    <List>
                        {filteredHistory.map((item, index) => (
                            <div key={item.id}>
                                <ListItem
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(43, 85, 135, 0.04)',
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        {getActionIcon(item.type)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="subtitle1" component="span">
                                                    {item.details}
                                                </Typography>
                                                <Chip
                                                    label={formatActionType(item.type)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `${getActionColor(item.type)}15`,
                                                        color: getActionColor(item.type),
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary' }}>
                                                {!projectName && (
                                                    <Typography variant="body2" component="span">
                                                        Project: {item.project}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" component="span">
                                                    User: {item.user}
                                                </Typography>
                                                <Typography variant="body2" component="span">
                                                    {formatTimestamp(item.timestamp)}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                {index < filteredHistory.length - 1 && <Divider />}
                            </div>
                        ))}
                        {filteredHistory.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                            {projectName 
                                                ? `No history entries found for ${projectName}`
                                                : 'No history entries match the selected filters'
                                            }
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>
                </Paper>
            </Box>
        </DashboardLayout>
    );
}

export default RunHistory; 